import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBookApi, getChaptersApi, getBookSpeciesApi, listSourcePreviewPagesApi, trackEvent, type ChapterNode, type SpeciesRef } from '../api';
import { toast } from '../toast';
import { purchaseUrl, purchasePrice, primaryPurchase, type PurchaseLink } from '../lib/purchase';
import './Book.css';

// 品种 emoji（按名称/分类粗分）
function speciesEmoji(s: { name: string; category?: string | null }): string {
  const n = s.name; const c = s.category ?? '';
  if (/虾/.test(n)) return '🦞';
  if (/蟹/.test(n)) return '🦀';
  if (/鱿|章鱼|乌贼|墨鱼/.test(n)) return '🦑';
  if (/贝|蛤|蚌|扇贝|牡蛎|螺/.test(n)) return '🐚';
  if (/海/.test(c)) return '🐠';
  return '🐟';
}

/** 把后端真实章节（order_no/title/页码区间）映射成目录视图模型。 */
function mapChapters(rows: ChapterNode[]): Chapter[] {
  return rows.map((c, i) => {
    const pages =
      c.page_start != null && c.page_end != null
        ? `PDF ${c.page_start}–${c.page_end} 页`
        : c.page_start != null
          ? `PDF ${c.page_start} 页起`
          : '';
    return {
      id: c.id,
      no: String(c.order_no ?? i + 1).padStart(2, '0'),
      title: c.title,
      m: '内容可在线查阅',
      tag: pages || '可查',
      tagClass: 'ok',
      pageStart: c.page_start ?? undefined,
    };
  });
}

function vendorClass(name: string): 'v1' | 'v2' | 'v3' | 'v4' {
  if (name.includes('当')) return 'v1';
  if (name.includes('京')) return 'v2';
  if (name.includes('天猫') || name.includes('猫')) return 'v3';
  return 'v4';
}
function vendorIcon(name: string): string {
  if (name.includes('当')) return '当';
  if (name.includes('京')) return '京';
  if (name.includes('天猫') || name.includes('猫')) return '天';
  if (name.includes('出版社') || name.includes('官方')) return '社';
  return name.slice(0, 1);
}

interface Chapter {
  id?: string;
  no?: string | number;
  title: string;
  m?: string;
  tag?: string;
  tagClass?: string;
  pageStart?: number;
}

interface RelatedSpecies {
  name: string;
  icon: string;
  sub: string;
}

interface Vendor {
  cls: string;
  ic: string;
  name: string;
  sub: string;
  price: string;
  url?: string;
}

interface BookView {
  id: string;
  title: string;
  cname: string;       // 封面短名
  cen: string;         // 封面英文
  pill: string;
  author: string;
  desc: string;
  cover: string;       // image URL or emoji fallback
  stats: { coverage: string; chunks: string; images: string; rating: string };
  chaptersCount?: number;
  catalogResourceCount?: number;
  chapters: Chapter[];
  related: RelatedSpecies[];
  vendors: Vendor[];
  json: {
    book_id: string;
    title: string;
    authors: string[];
    publisher: string;
    isbn: string;
    chunks: number;
    images: number;
    coverage: number;
    species_links: string[];
    purchase: PurchaseLink[];
  };
}

const DEFAULT_BOOK: BookView = {
  id: '',
  title: '正在加载图书资料…',
  cname: '图书资料',
  cen: 'BOOK · CN',
  pill: '📖 图书资料',
  author: '正在读取出版社图书资料…',
  desc: '这里会展示图书简介、章节、原书图片和相关水产品种。',
  cover: '📘',
  stats: { coverage: '—', chunks: '—', images: '—', rating: '' },
  chapters: [],
  related: [],
  vendors: [],
  json: {
    book_id: '',
    title: '',
    authors: [],
    publisher: '',
    isbn: '',
    chunks: 0,
    images: 0,
    coverage: 0,
    species_links: [],
    purchase: [],
  },
};

function mergeApi(api: any, fallback: BookView): BookView {
  if (!api || typeof api !== 'object') return fallback;
  const chapters: Chapter[] = Array.isArray(api.chapters) && api.chapters.length
    ? api.chapters.map((c: any, i: number) => ({
        id: c.id,
        no: c.no ?? String(i + 1).padStart(2, '0'),
        title: c.title ?? c.name ?? '—',
        m: c.summary ?? (c.chunks != null ? `${c.chunks} 条资料` : ''),
        tag: c.tag ?? '可查',
        tagClass: c.tag_class ?? 'ok',
      }))
    : fallback.chapters;

  const authors: string[] = Array.isArray(api.authors) ? api.authors : (api.author ? [api.author] : fallback.json.authors);
  const year = api.publish_date ? String(api.publish_date).slice(0, 4) : '';
  const authorLine = [
    authors[0] ? `${authors[0]}${authors.length > 1 ? ' 等' : ''} 编著` : null,
    api.publisher,
    year,
    api.isbn ? `ISBN ${api.isbn}` : '',
  ].filter(Boolean).join(' · ') || '出版信息暂未提供';

  // Only display configured facts; never invent original prices, stock or seller qualifications.
  const purchaseLinks: PurchaseLink[] = Array.isArray(api.purchase_links) ? api.purchase_links : [];
  const vendors: Vendor[] = purchaseLinks.map((l) => ({
        cls: vendorClass(l.vendor),
        ic: vendorIcon(l.vendor),
        name: l.vendor,
        sub: purchasePrice(l) ? '价格以购买页面为准' : '前往销售页面查看详情',
        price: purchasePrice(l) ?? '价格以购买页面为准',
        url: l.url,
      }));
  const titleShort = (api.title ?? fallback.title).replace(/^中国/, '').slice(0, 8);
  const isNationalCatalog = ['9787109319387', '9787109319394'].includes(api.isbn ?? '');

  return {
    ...fallback,
    id: api.id ?? api.book_id ?? fallback.id,
    title: api.title ?? fallback.title,
    cname: titleShort || fallback.cname,
    author: authorLine,
    desc: api.intro ?? api.desc ?? fallback.desc,
    cover: api.cover_url ?? api.cover ?? fallback.cover,
    // 统计全部用真实计数（后端 chunks_count/images_count；覆盖品种在渲染时取真实关联品种数）。
    stats: {
      coverage: fallback.stats.coverage,
      chunks: api.chunks_count != null ? String(api.chunks_count) : fallback.stats.chunks,
      images: api.images_count != null ? String(api.images_count) : fallback.stats.images,
      rating: '',
    },
    chaptersCount: api.chapters_count ?? undefined,
    catalogResourceCount: isNationalCatalog ? (api.chunks_count ?? undefined) : undefined,
    chapters,
    vendors,
    json: {
      ...fallback.json,
      title: api.title ?? fallback.title,
      book_id: api.id ?? api.book_id ?? fallback.json.book_id,
      authors,
      publisher: api.publisher ?? fallback.json.publisher,
      isbn: api.isbn ?? fallback.json.isbn,
      chunks: api.chunks_count ?? fallback.json.chunks,
      images: api.images_count ?? fallback.json.images,
      purchase: purchaseLinks,
    },
  };
}

export default function BookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [book, setBook] = useState<BookView>(DEFAULT_BOOK);
  const [relatedSpecies, setRelatedSpecies] = useState<SpeciesRef[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [sourcePages, setSourcePages] = useState<number[]>([]);

  useEffect(() => {
    if (!id) return;
    trackEvent('book_impression', { book_id: id, path: `/book/${id}`, source: 'book_detail' });
    let cancelled = false;
    setBook(DEFAULT_BOOK);
    setRelatedSpecies([]);
    setLoadFailed(false);
    setSourcePages([]);
    // 图书详情 / 章节目录 / 关联品种并行拉取。详情失败时不回退样例资料。
    Promise.all([
      getBookApi(id).catch(() => null),
      getChaptersApi(id).catch(() => [] as ChapterNode[]),
      getBookSpeciesApi(id).catch(() => [] as SpeciesRef[]),
      listSourcePreviewPagesApi(id).catch(() => [] as number[]),
    ]).then(([api, chapters, species, pages]) => {
      if (cancelled) return;
      if (!api) {
        setLoadFailed(true);
        setBook({
          ...DEFAULT_BOOK,
          title: '图书资料暂不可用',
          desc: '暂时无法读取这本图书的资料，请返回图书列表后重试。',
          author: '图书服务暂不可用',
        });
        return;
      }
      const merged = mergeApi(api, DEFAULT_BOOK);
      if (Array.isArray(chapters) && chapters.length) {
        merged.chapters = mapChapters(chapters);
      }
      if (Array.isArray(species)) {
        merged.json.species_links = species.map((item) => item.name);
        merged.json.coverage = species.length;
        merged.stats.coverage = String(species.length);
      }
      setBook(merged);
      if (Array.isArray(species)) setRelatedSpecies(species);
      setSourcePages(pages);
    });
    return () => { cancelled = true; };
  }, [id]);

  const preferredPurchase = primaryPurchase(book.json.purchase);
  const preferredUrl = purchaseUrl(preferredPurchase?.url);
  const isCatalogBook = book.catalogResourceCount != null;
  const sourcePageSet = new Set(sourcePages);

  return (
    <div className="container">
      <div className="book-hero">
        <div className="cover-wrap">
          <BookCover book={book} />
        </div>
        <div className="meta">
          <span className="pill">{book.pill}</span>
          <h1>{book.title}</h1>
          <div className="sub">{book.author}</div>
          <p className="desc">{book.desc}</p>
          <div className="stats-inline">
            <div><div className="s">{isCatalogBook ? '目录资源' : '关联品种'}</div><div className="v">{isCatalogBook ? book.catalogResourceCount : (relatedSpecies.length || book.stats.coverage)}</div></div>
            <div><div className="s">资料条目</div><div className="v">{book.stats.chunks}</div></div>
            <div><div className="s">图片资源</div><div className="v">{book.stats.images}</div></div>
            <div><div className="s">章节数</div><div className="v">{book.chaptersCount ?? book.chapters.length}</div></div>
          </div>
          <div className="actions">
            {preferredUrl && !loadFailed ? (
              <a
                className="btn primary"
                href={preferredUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('book_jump', {
                  book_id: book.id,
                  path: `/book/${book.id}`,
                  metadata: { vendor: preferredPurchase?.vendor ?? '' },
                })}
              >🛒 前往购买{preferredPurchase && purchasePrice(preferredPurchase) ? ` ${purchasePrice(preferredPurchase)}` : ''}</a>
            ) : (
              <button className="btn primary" disabled>暂无购买渠道</button>
            )}
            <Link
              className="btn accent"
              to={`/chat?q=${encodeURIComponent('我想了解《' + book.title + '》中的内容')}`}
              style={{ textDecoration: 'none' }}
            >💬 在问答中使用</Link>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: book.vendors.length > 0 ? '1.7fr 1fr' : '1fr' }}>
        <div>
          <div className="section-title"><h2>📑 章节目录</h2><span className="hint">共 {book.stats.chunks} 条可查资料</span></div>
          {sourcePages.length > 0 && (
            <div className="card source-page-picker">
              <strong>原书 PDF 按资料定位</strong>
              <p>章节入口直接进入正文，可连续翻页或按品种跳转；品种档案打开该品种的正文单页。目录页码为 PDF 页码，可能与书内印刷页码不同。</p>
            </div>
          )}
          <div className="card tocList">
            {book.chapters.length === 0 && <div className="empty-state">暂无可展示的章节资料。</div>}
            {book.chapters.map((c, i) => (
              <div key={c.id ?? i} className="item">
                <div className="no">{c.no}</div>
                <div><div className="t">{c.title}</div><div className="m">{c.m}</div></div>
                <div className="toc-page-actions">
                  <span className={`tag${c.tagClass ? ' ' + c.tagClass : ''}`}>{c.tag}</span>
                  {c.pageStart != null && sourcePageSet.has(c.pageStart) && (
                    <Link className="source-page-link" to={`/source-page/${book.id}/${c.pageStart}?chapter=${encodeURIComponent(c.id ?? '')}`} target="_blank" rel="noopener noreferrer" aria-label="阅读章节原文（新窗口）">阅读章节原文 ↗</Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="section-title">
            <h2>🐠 {isCatalogBook ? '目录资源（节选）' : '关联核心品种'}</h2>
            <span className="hint">{isCatalogBook ? `全书 ${book.catalogResourceCount} 条 · 当前展示 ${relatedSpecies.length} 条` : `关联资料${relatedSpecies.length ? ` · ${relatedSpecies.length} 个` : ''}`}</span>
          </div>
          <div className="card species-mini">
            {relatedSpecies.length > 0
              ? relatedSpecies.map((s) => (
                  <Link key={s.id} to={`/species/${s.id}`} className="m" style={{ textDecoration: 'none' }}>
                    <div className="ico">{speciesEmoji(s)}</div>
                    <div><b>{s.name}</b><div className="s">{s.category ?? '品种'}</div></div>
                  </Link>
                ))
              : book.related.map((s, i) => (
                  <div key={i} className="m">
                    <div className="ico">{s.icon}</div>
                    <div><b>{s.name}</b><div className="s">{s.sub}</div></div>
                  </div>
                ))}
            {relatedSpecies.length === 0 && book.related.length === 0 && <div className="empty-state">暂无关联品种资料。</div>}
          </div>

        </div>

        {book.vendors.length > 0 && <div>
          <div className="section-title"><h2>🛒 购买渠道</h2></div>
          <div className="card buy-row">
            {book.vendors.map((v, i) => (
              <div key={i} className={`vendor ${v.cls}`}>
                <div className="l">
                  <div className="ico">{v.ic}</div>
                  <div><b>{v.name}</b><div className="muted small">{v.sub}</div></div>
                </div>
                <div>
                  <div className="price" style={v.price === '价格以购买页面为准' ? { fontSize: 13 } : undefined}>{v.price}</div>
                  {purchaseUrl(v.url) ? (
                    <a
                      className="btn sm primary mt-8"
                      href={purchaseUrl(v.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('book_jump', {
                        book_id: book.id,
                        path: `/book/${book.id}`,
                        metadata: { vendor: v.name },
                      })}
                    >前往 →</a>
                  ) : (
                    <button className="btn sm primary mt-8" type="button" onClick={() => toast('该渠道购买链接尚未配置')}>暂无链接</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>}
      </div>

      <div className="footer">©2026 上海渔联网科技 · 科普趣答</div>
    </div>
  );
}

function BookCover({ book }: { book: BookView }) {
  const [failed, setFailed] = useState(false);
  const hasImage = /^(?:https?:|\/)/.test(book.cover) && !failed;

  useEffect(() => setFailed(false), [book.cover]);

  return (
    <div className={`cover${hasImage ? ' book-cover-real' : ''}`}>
      {hasImage ? (
        <img
          className="book-cover-image"
          src={book.cover}
          alt={`《${book.title}》封面`}
          onError={() => setFailed(true)}
        />
      ) : (
        <>
          <div className="icon">{book.cover}</div>
          <div className="cname">{book.cname}</div>
          <div className="cen">{book.cen}</div>
        </>
      )}
    </div>
  );
}
